import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  ArrowLeft,
  Save,
  Send,
  Loader2,
  FileText
} from 'lucide-react';

const NewContract = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contractTerms, setContractTerms] = useState(['1yr', '2yr', '3yr', '4yr', '5yr']);
  
  const [formData, setFormData] = useState({
    title: '',
    document_type: 'contract',
    content: '',
    client_id: '',
    template_id: templateId || '',
    requires_signature: true,
    start_date: '',
    end_date: '',
    contract_term: '',
    value: '',
    currency: 'USD',
    fields_data: {}
  });

  useEffect(() => {
    fetchClients();
    fetchTemplates();
    fetchContractTerms();
    
    if (templateId) {
      loadTemplate(templateId);
    }
  }, [templateId]);

  const fetchClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data || []);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/contract-templates');
      console.log('Templates API response:', res.data);
      setTemplates(res.data.templates || []);
      if (res.data.templates && res.data.templates.length > 0) {
        toast.success(`Loaded ${res.data.templates.length} templates`);
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    }
  };

  const fetchContractTerms = async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.contract_terms && res.data.contract_terms.length > 0) {
        setContractTerms(res.data.contract_terms);
      }
    } catch (error) {
      console.error('Failed to fetch contract terms:', error);
      // Keep default terms if fetch fails
    }
  };

  const loadTemplate = async (templateId) => {
    setLoading(true);
    try {
      const res = await api.get(`/contract-templates/${templateId}`);
      const template = res.data.template;
      
      if (template) {
        setFormData(prev => ({
          ...prev,
          template_id: templateId,
          content: template.content || '',
          document_type: template.category === 'amendment' ? 'contract' : template.category,
          title: template.name || ''
        }));
        toast.success('Template loaded successfully');
      }
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load template: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // If client is selected, update template variables
      if (field === 'client_id' && value) {
        const selectedClient = clients.find(c => c.id === value);
        if (selectedClient && updated.content) {
          updated.content = replaceTemplateVariables(updated.content, selectedClient, updated);
        }
      }
      
      return updated;
    });
  };
  
  const replaceTemplateVariables = (content, client, formData) => {
    let updatedContent = content;
    
    // Replace client variables
    updatedContent = updatedContent.replace(/\[CLIENT_NAME\]/g, client.company_name || '');
    updatedContent = updatedContent.replace(/\[CLIENT_EMAIL\]/g, client.email || client.contact_email || '');
    updatedContent = updatedContent.replace(/\[CLIENT_CONTACT\]/g, client.contact_name || '');
    updatedContent = updatedContent.replace(/\[CLIENT_ADDRESS\]/g, client.address || '');
    updatedContent = updatedContent.replace(/\[CLIENT_PHONE\]/g, client.phone || '');
    
    // Replace contract variables
    if (formData.start_date) {
      updatedContent = updatedContent.replace(/\[START_DATE\]/g, new Date(formData.start_date).toLocaleDateString());
    }
    if (formData.end_date) {
      updatedContent = updatedContent.replace(/\[END_DATE\]/g, new Date(formData.end_date).toLocaleDateString());
    }
    if (formData.value) {
      updatedContent = updatedContent.replace(/\[CONTRACT_VALUE\]/g, `${formData.currency} ${formData.value}`);
    }
    
    return updatedContent;
  };

  const handleContractTermChange = (term) => {
    if (!formData.start_date) {
      toast.error('Please select a start date first');
      return;
    }

    // Calculate end date based on term
    const startDate = new Date(formData.start_date);
    const years = parseInt(term.replace('yr', ''));
    const endDate = new Date(startDate);
    endDate.setFullYear(endDate.getFullYear() + years);
    
    // Format to YYYY-MM-DD
    const formattedEndDate = endDate.toISOString().split('T')[0];
    
    setFormData(prev => ({
      ...prev,
      contract_term: term,
      end_date: formattedEndDate
    }));
    
    toast.success(`End date set to ${years} year${years > 1 ? 's' : ''} from start date`);
  };

  const handleSubmit = async (status = 'draft') => {
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a contract title');
      return;
    }

    // Check if content is empty (Quill returns <p><br></p> for empty)
    const cleanContent = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!cleanContent) {
      toast.error('Please enter contract content');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        status,
        value: formData.value ? parseFloat(formData.value) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      const res = await api.post('/contracts', payload);
      toast.success('Contract created successfully');
      navigate(`/contracts/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setFormData(prev => ({
        ...prev,
        template_id: templateId,
        content: template.content,
        document_type: template.category === 'amendment' ? 'contract' : template.category,
        title: prev.title || template.name
      }));
      toast.success('Template loaded');
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/contracts')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Contracts
          </Button>
        </div>
        <h1 className="text-2xl font-bold text-slate-800">Create New Contract</h1>
        <p className="text-sm text-slate-500 mt-1">
          Fill in the details to create a new contract document
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Contract Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Annual Support Agreement 2024"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="document_type">Document Type *</Label>
                  <Select
                    value={formData.document_type}
                    onValueChange={(value) => handleChange('document_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="contract">Contract</SelectItem>
                      <SelectItem value="work_order">Work Order</SelectItem>
                      <SelectItem value="project_document">Project Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="client_id">Client</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(value) => handleChange('client_id', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Client Information Card */}
              {formData.client_id && (() => {
                const selectedClient = clients.find(c => c.id === formData.client_id);
                return selectedClient ? (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-base font-semibold text-blue-900 flex items-center">
                        <FileText className="w-4 h-4 mr-2" />
                        Selected Client Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-600 font-medium">Company Name</p>
                          <p className="text-sm text-slate-900">{selectedClient.company_name}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">Email</p>
                          <p className="text-sm text-slate-900">{selectedClient.email || selectedClient.contact_email || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">Contact Name</p>
                          <p className="text-sm text-slate-900">{selectedClient.contact_name || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">Phone</p>
                          <p className="text-sm text-slate-900">{selectedClient.phone || 'N/A'}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-slate-600 font-medium">Address</p>
                          <p className="text-sm text-slate-900">{selectedClient.address || 'N/A'}</p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-3 bg-blue-100 p-2 rounded">
                        ℹ️ This information will be used to replace template variables like [CLIENT_NAME], [CLIENT_EMAIL], etc.
                      </p>
                    </CardContent>
                  </Card>
                ) : null;
              })()}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <div className="flex gap-2">
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleChange('end_date', e.target.value)}
                      className="flex-1"
                    />
                    <Select
                      value={formData.contract_term || ''}
                      onValueChange={(value) => handleContractTermChange(value)}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue placeholder="Quick set" />
                      </SelectTrigger>
                      <SelectContent>
                        {contractTerms.map(term => {
                          const years = parseInt(term.replace('yr', ''));
                          return (
                            <SelectItem key={term} value={term}>
                              {years} Year{years > 1 ? 's' : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="value">Contract Value</Label>
                  <Input
                    id="value"
                    type="number"
                    placeholder="0.00"
                    value={formData.value}
                    onChange={(e) => handleChange('value', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => handleChange('currency', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="AUD">AUD</SelectItem>
                      <SelectItem value="CAD">CAD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_signature"
                  checked={formData.requires_signature}
                  onChange={(e) => handleChange('requires_signature', e.target.checked)}
                  className="rounded"
                />
                <Label htmlFor="requires_signature" className="cursor-pointer">
                  Requires signature
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardHeader>
              <CardTitle>Contract Content *</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-white">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(value) => handleChange('content', value)}
                  placeholder="Enter contract content here..."
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      [{ 'align': [] }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                  style={{ minHeight: '300px' }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Use the toolbar above to format your contract content
              </p>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button
              onClick={() => handleSubmit('draft')}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save as Draft
                </>
              )}
            </Button>
            <Button
              onClick={() => handleSubmit('pending_signature')}
              disabled={loading || !formData.requires_signature}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Create & Send
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Use Template</CardTitle>
            </CardHeader>
            <CardContent>
              {templates.length > 0 ? (
                <>
                  <Select
                    value={formData.template_id}
                    onValueChange={handleUseTemplate}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-2">
                    Load content from an existing template
                  </p>
                </>
              ) : (
                <div className="text-sm text-slate-500">
                  <p>No templates available.</p>
                  <p className="mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => navigate('/contracts/templates')}
                      className="p-0 h-auto text-blue-600"
                    >
                      Create a template
                    </Button> to get started.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-slate-600">
              <p>• Use templates to save time</p>
              <p>• HTML tags are supported in content</p>
              <p>• Save as draft to continue later</p>
              <p>• Link to a client for easy tracking</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewContract;
